import _ from 'lodash'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import unwrapModel from 'scoreboard/components/unwrap-model'
import deepEqual from 'deep-equal'
import Dataset from 'scoreboard/datasets/model/dataset'
const { BLANK } = Dataset
import { to24hrTime } from 'scoreboard/components/date-time-util'
import { isDayOfTheWeek, isMonthOfTheYear } from './is-compare-all-time'
import isGeographicOrRollup from 'scoreboard/datasets/is-geographic-or-rollup'
import DataType from 'scoreboard/datasets/model/data-type'
const { TEXT, YES_NO, DATE, TIME, DATE_TIME, CALENDAR_PERIOD, CLUSTER } = DataType
import { dataSuperTypesById } from 'scoreboard/datasets/edit/fields/data-super-types'
import * as DatasetsTabularResult from './overview/datasets-tabular-result'


/**
 * Applies a drilldown to a dataset table.  May revise existing filters or add
 * new ones
 *
 * @param {Object} field the main field of the field table
 * @param {Array} filters the filters which are already applied
 * @param {DatasetTable} table the table where the drilldown is being applied
 * @param {String|Number} value the value to drill down to
 * @return {Array} a revised list of filters
 */
function applyFilter (field, filters = [], table, value) {
	field = _.get(field, 'attributes', field)
	let filter = {
		fieldId: table.get('fieldId'),
		visibleLinkedFieldId: table.get('visibleLinkedFieldId')
	}

	// Note: yes-no handling needs to be before the more generic BLANK check
	// because yes-no no longer has an `is-blank` operator
	if (field.dataType === YES_NO) {
		if (_.isBoolean(value)) {
			value = value ? 'yes' : 'no'
		}
		if (value === 0) value = 'no'
		if (value === 1) value = 'yes'
		if (value === 'yes' || value === 'no' || value === DatasetsTabularResult.BLANK) {
			_.extend(filter, {
				operator: 'is-any',
				values: [ value ]
			})
		}
		return filters.concat(filter)
	}

	if (value === DatasetsTabularResult.BLANK) {
		if (dataSuperTypesById[DATE_TIME].dataTypes.includes(field.dataType) ||
			isGeographicOrRollup(field.dataType)) {
			filter.operator = 'is-blank'
		} else {
			_.extend(filter, {
				operator: 'is-any',
				values: [ BLANK ]
			})
		}
		return filters.concat(filter)
	}

	if (value === DatasetsTabularResult.NOT_IN_ANY) {
		filter.operator = _.includes([
			DATE, DATE_TIME
		], field.dataType) ?
			'not-in-any-period' :
			'not-in-any-range'
		return filters.concat(filter)
	}

	if (_.includes([DATE, DATE_TIME, CALENDAR_PERIOD], field.dataType)) {
		if (isDayOfTheWeek(table)) {
			filter.operator = 'on-day-of-the-week'
			filter.days = [value]
		} else if (isMonthOfTheYear(table)) {
			filter.operator = 'in-month-of-the-year'
			filter.months = [value]
		} else if (table.get('summaryType') === 'similar-time-ranges') {
			filter.operator = 'in-any-period-index'

			let grouping = _.find(table.get('groupings'), g => {
				return g.summaryType === 'similar-time-ranges'
			})

			if (grouping) {
				filter.periodIndexes = [value]
				filter.groupCalendarId = grouping.groupCalendarId
				filter.breakdownCalendarId = grouping.breakdownCalendarId
			} else {
				// for me the grouping was always null, but I don't want to
				// disturb the above code in case it does something (#45273)
				filter.periodIndexes = [value]
				filter.groupCalendarId = unwrapModel(table).groupCalendarId
				filter.breakdownCalendarId = unwrapModel(table).breakdownCalendarId
			}
		} else {
			filter.operator = 'in-any-period'
			filter.periodIds = [value]
		}
		return filters.concat(filter)
	}

	if (field.dataType === TIME) {
		if (table.get('summaryType') === 'hour-of-the-day') {
			filter.operator = 'between-date-time'
			const intValue = parseInt(value)
			filter.time1 = to24hrTime({
				hour: intValue
			})
			filter.time2 = to24hrTime({
				hour: (intValue + 1) % 24
			})
			filter.updateType1 = 'manual'
			filter.updateType2 = 'manual'
		} else {
			filter.operator = 'at-any-time'
			filter.times = [value]
		}
		return filters.concat(filter)
	}

	if (field.dataType === TEXT || field.dataType === CLUSTER) {
		// the first drilldown for text fields with ranges will be to a specific
		// range.  subsequent drilldowns can use the normal behavior
		if (!_.isEmpty(field.ranges) &&
			(_.isEmpty(table.get('summaryType')) || table.get('summaryType') === 'range')) {
			table.set('summaryType', 'value')
			filter.operator = 'in-the-range'
			filter.rangeIds = [ value ]
			return filters.concat(filter)
		} else {
			return matchAndApply(filters, table.get('fieldId'),
				table.get('visibleLinkedFieldId'),
				'in-the-range', filter => {
					filter.operator = 'is-any'
					filter.values = [value]
				})
		}
	}

	if (isGeographicOrRollup(field.dataType)) {
		return rollupDrilldown({
			filter,
			filters,
			value,
			targetDrilldownFieldId: table.get('fieldId')
		})
	}

	// the first drilldown for numeric fields with ranges will be to a specific
	// range
	if (isNumericDataType(field.dataType) &&
		!_.isEmpty(field.ranges) &&
		value != DatasetsTabularResult.BLANK &&
		table.get('summaryType') === 'range') {
		return matchAndApply(filters, table.get('fieldId'),
			table.get('visibleLinkedFieldId'), 'is-any', filter => {
				if (_.isEmpty(filter.rangeIds)) {
					table.set('summaryType', 'value')
					filter.operator = 'in-the-range'
					filter.rangeIds = [value]
				}
		})
	}

	if (isNumericDataType(field.dataType) && table.get('summaryType') == 'value') {
		// remove any existing in-the-range filter
		return matchAndApply(filters, table.get('fieldId'),
			table.get('visibleLinkedFieldId'), 'in-the-range', filter => {
				filter.operator = 'is-any'
				filter.values = [value]
			})
	}

	// I put a return statement that should cover every condition to ease code
	// readability so we shouldn't ever reach here, but just in case assume
	// nothing changed
	return filters
}

/**
 * Performs a drilldown on a rollup field. Only should be used if you know
 * the field is a rollup data type
 *
 * @param {Integer} targetDrilldownFieldId - ID of the rollup field
 *
 * @param {ApiDatasetFilter} filter - initial settings for the filter (like fieldId)
 *
 * @param {ApiDatasetFilter[]} filters - any filters that already exist. May be a
 * mix of filters already applied to drilldown field and filters that apply to
 * other fields
 *
 * @param [Integer|Integer[]]] value - ID or IDs that were drilled down on
 * (usually a dataset tree id)
 *
 * @returns {ApiDatasetFilter[]} - list of filters with the drilldown applied
 */
function rollupDrilldown ({ targetDrilldownFieldId, filter,  filters, value }) {
	let dataToMatch = {
		fieldId: targetDrilldownFieldId,
		operator: 'is-any'
	}
	let filtersByLinkGroup = _(filters)
		.filter(f => _.isNil(f.permissionId))
		.groupBy('linkGroup')
		.value()
	let match = matchingFilter(filters, dataToMatch, f =>
		_.isNil(f.permissionId) &&
		_.size(filtersByLinkGroup[f.linkGroup]) == 1)
	if (match) {
		filter = match
	}
	// filter on the tree and its descendants
	filter.operator = 'is-any'
	filter.rollupIsAnyFilters = {}

	if (!_.isNil(value)) {
		let values = _.isArray(value) ? value : [value]
		values.forEach(value => {
			filter.rollupIsAnyFilters[value] = 'item-rollups-and-descendants'
		})
	}
	if (match) {
		return filters
	} else {
		return filters.concat(filter)
	}
}

/**
 * Handles clicking on the Back button when viewing a dataset table.
 *
 * @param {Object} field the main field of the field table
 * @param {Array} filters the filters which are already applied
 * @param {Object} table the table where the drilldown is being applied
 * @param {DatasetsQueryResult} result - results of the dataset query that
 * rendered this table
 *
 * @return the new filters that should be displayed
 */
function back (field, filters, table, result) {
	field = unwrapModel(field)

	function applyBack (filter) {
		if (field.dataType === TEXT) {
			// if the user drilled down on a range
			if (filter.operator === 'in-the-range' && _.size(filter.rangeIds) === 1 &&
				table.get('summaryType') === 'value') {
				// go back to viewing the ranges
				table.set('summaryType', 'range')
				// handle backing out of clicks on a range and then a text value
				// within that range
			} else if (filter.operator === 'is-any' && filter.values.length === 1 &&
				_.size(filter.rangeIds) === 1) {
				delete filter.values
				filter.operator = 'in-the-range'
				return filters
			}
		}

		if (isGeographicOrRollup(field.dataType)) {
			return applyRollupBack({
				underlyingField: field,
				filter,
				filters,
				parentId: result.parentId
			})
		}

		if (isNumericDataType(field.dataType)) {
			// if the user drilled down on a range
			if (filter.operator === 'in-the-range' && _.size(filter.rangeIds) === 1 &&
				table.get('summaryType') === 'value') {
				// go back to viewing the ranges
				table.set('summaryType', 'range')
				// handle backing out of clicks on a range and then a numeric value
				// within that range
			} else if (filter.operator === 'is-any' && _.size(filter.rangeIds) === 1) {
				delete filter.values
				filter.operator = 'in-the-range'
				return filters
			}
		}

		return null
	}

	return doBack({
		fieldId: table.get('fieldId'),
		underlyingField: field,
		filters,
		applyBack
	})
}

/**
 * Handles the "back" action on a rollup-based set of filters (eg a treemap chart)
 *
 * @param {Int} fieldId - the ID of the primary dataset field; must be a rollup field
 *
 * @param {DatasetField} underlyingField - the underlying field for fieldId (could be
 * different if it's a linked field)
 *
 * @param {ApiDatasetFilter[]} filters - filters that are currently being used
 *
 * @param {Int} [parentId] the parent ID of the current rollup tree data. null indicates
 * that the parent is the root, so the relevant filter will be removed
 *
 * @returns {ApiDatasetFilter[]} - list of filters with the "back" action applied
 */
function rollupBack ({ fieldId, underlyingField, filters, parentId}) {

	function applyBack (filter) {
		return applyRollupBack( { underlyingField, filter, filters, parentId })
	}

	return doBack({
		fieldId,
		underlyingField,
		filters,
		applyBack
	})
}

function applyRollupBack ({ underlyingField, filter, filters, parentId }) {
	if (filter.operator === 'is-any') {
		if (!parentId ||
			// if the parent is the root
			(!!underlyingField && parentId == underlyingField.treeId)) {
			// fall back to behavior below and remove the filter
		} else {
			// modify existing filter so it's based on the parent
			filter.rollupIsAnyFilters = {}
			filter.rollupIsAnyFilters[parentId] = 'item-rollups-and-descendants'
			return filters
		}
	}

	return null
}

function doBack ({ fieldId, underlyingField, filters, applyBack }) {
	underlyingField = unwrapModel(underlyingField)

	let matchedFilter = matchingFilter(filters, { fieldId }, f => _.isNil(f.permissionId))

	let result = applyBack(matchedFilter)
	if (result) {
		return result
	}

	// If applyBack didn't return an updated filter, then just remove the filter
	// from the filters array (basically going "all the way" back)
	return _.difference(filters, [matchedFilter])
}

/**
 * Finds the first filter with attributes that match those in dataToMatch
 * @param {Array} filters the filters to search
 * @param {Object} dataToMatch the data to match
 * @param {Function} [allowable] allows specifying conditions where a
 * filter should not be considered a match.  The function is provided the filter
 * as an argument, and must return true to indicate the filter is an eligible
 * match, or false to indicate it is not
 */
function matchingFilter(filters, dataToMatch, allowable) {
	return _.find(filters, (filter) => {
		let match = true
		if (allowable) {
			match = allowable(filter)
		}
		_.each(_.keys(dataToMatch), (key) => {
			if (!deepEqual(filter[key], dataToMatch[key])) {
				match = false
			}
		})
		return match
	})
}

/**
 * Tries to find a filter matching the given fieldId and operator, but no
 * permissionId.  If one is found, modifies that filter.  Otherwise, creates a
 * new filter.
 *
 * @param {Array} filters the current filters
 * @param {Number} fieldId the fieldId for the filter
 * @param {String} operator the operator for the filter
 * @param callback a callback used to setup the correct attributes for the
 * revised or newly created filter
 * @returns {Array} a revised list of filters
 */
function matchAndApply(filters, fieldId, visibleLinkedFieldId, operator, callback) {
	let dataToMatch = { fieldId, visibleLinkedFieldId, operator }
	let match = matchingFilter(filters, dataToMatch, f => _.isNil(f.permissionId))
	if (match) {
		// any filter we modify will be valid
		match.valid = true
		// any filter we modify should be considered a new filter
		delete match.id
		callback(match)
		return filters
	} else {
		let filter = { fieldId, visibleLinkedFieldId }
		callback(filter)
		return filters.concat(filter)
	}
}

_.extend(applyFilter, {
	back,
	matchingFilter,
	rollupDrilldown,
	rollupBack
})

export default applyFilter
export { back }
export { matchingFilter }
export { rollupDrilldown }
export { rollupBack }

applyFilter.__filename__ = 'datasets/apply-filter.js'
